//MarksTrackerList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
// import logo from '../../assets/logo.svg';

const MarksTrackerList = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const location = useLocation();
  const [marksTracker, setMarksTracker] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedMarksTrackerId, setSelectedMarksTrackerId] = useState(null);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [deletedFailed, setDeletedFailed] = useState(false);

  useEffect(() => {
    document.title = authState.isLoggedIn ? `${authState.user.name}'s dashboard` : "Think5Whys";
  }, [authState]);

  useEffect(() => {
    const config = {
      url: "/markstracker/myall",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id}
    };
    // console.log("My MarksTracker Search Payload: ", config);
  
    fetchData(config, { showSuccessToast: false }).then(data => {
      // console.log("Response from API:", data);
      setMarksTracker(data.marksTrackerData || []); 
    });
  }, [authState.token, fetchData, authState.user._id]);
  
  const deleteClicked = () => {
    setIsDeleteClicked(true);
  }
  
  const noDeleteClicked = () => {
    setIsDeleteClicked(false);
  }

  const handleDelete = async (marksTrackerId) => {
    try {
        const config = {
            url: `/markstracker/${marksTrackerId}`,
            method: "delete",
            headers: { Authorization: authState.token }
        };
        // console.log("handleDelete:", config);

        const response = await axios(config); 
        // console.log("Response:", response);

        if (response.status === 200) {
            setMarksTracker((prev) => prev.filter((item) => item._id !== id));
        }
    } catch (error) {
        console.error("Error deleting MarksTracker:", error.response?.data || error.message);
        setDeletedFailed(true);
    }
};


  const handleView = (marksTrackerId) => {
    setSelectedMarksTrackerId(prevId => (prevId === marksTrackerId ? null : marksTrackerId));
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const sortedMarksTracker = Array.isArray(marksTracker)? [...marksTracker].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)): [];

  return (
    <div className="min-h-screen bg-black py-2">
      <div className='border-b border-blue-400 mb-2'>
          <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 mb-2 p-2">
              📖 My Marks Tracker
          </h2>
      </div>

      <div className="min-h-[80vh] bg-black rounded-lg p-6 shadow-lg">
        {marksTracker && marksTracker.length !== 0 && (
          <div className="flex justify-between items-center border-b border-b-gray-600 pb-4">
            <h3 className="text-yellow-200 text-lg">{marksTracker.length} Found!</h3>
            <Link to="/marks-tracker/add" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700">
              <FontAwesomeIcon icon={faPlus} /> Add New
            </Link>
          </div>
        )}

        {loading ? (
          <Loader />
        ) : marksTracker && marksTracker.length === 0 ? (
          <div className="h-[300px] flex flex-col items-center justify-center text-yellow-300">
            <h3 className="text-lg text-orange-400">No Record Found!</h3>
            <Link to="/marks-tracker/add" className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700">
              <FontAwesomeIcon icon={faPlus} /> Add New
            </Link>
          </div>
        ) : (
          sortedMarksTracker.map((marksTracker, index) => (
            <div key={marksTracker._id} className="bg-gray-900 text-white my-4 p-4 rounded-lg shadow-md">
              <div className="flex items-center gap-4">
                <span className="font-medium text-lg">{index + 1}. {marksTracker.examTitle}</span>
                <div className="ml-auto flex gap-4">
                  <Tooltip text="View details" position="top">
                    <span className="text-blue-500 cursor-pointer" onClick={() => handleView(marksTracker._id)}>
                      <i className="fa-solid fa-eye"></i>
                    </span>
                  </Tooltip>
                  <Tooltip text="Edit this record" position="top">
                    <Link to={`/marks-tracker/${marksTracker._id}`} className="text-green-500">
                      <i className="fa-solid fa-pen"></i>
                    </Link>
                  </Tooltip>
                  <Tooltip text="Delete this record" position="top">
                    <span className="text-red-500 cursor-pointer" onClick={deleteClicked}>
                      <i className="fa-solid fa-trash"></i>
                    </span>
                  </Tooltip>
                </div>
              </div>

              {selectedMarksTrackerId === marksTracker._id && (
                <div className="bg-gray-700 text-white mt-4 p-4 rounded-lg">
                  <p><strong className="text-yellow-400">MarksTracker Code:</strong> {marksTracker.marksTrackerCode}</p>
                  <p><strong>Exam Title:</strong> {marksTracker.examTitle}</p>
                  <p><strong>Exam Date:</strong> {marksTracker.examDate ? formatDateTime(marksTracker.examDate) : 'N/A'}</p>
                  <p><strong>Subject & Marks:</strong></p>
                  {marksTracker.subjects && marksTracker.subjects.length > 0 ? (
                    marksTracker.subjects.map((subject, subindex) => (
                      <p key={subindex}>
                        {subindex + 1}. {subject.name} [{subject.marksobtained}/{subject.fullmarks}]: {subject.percentmarks}%
                      </p>
                    ))
                  ) : (
                    <p>No subjects available.</p>
                  )}
                  <p><strong>Strongest Subject:</strong>{marksTracker.strongestSubject}</p>
                  <p><strong>Weakest Subject:</strong>{marksTracker.weakestSubject}</p>

                  <p><strong>Remarks:</strong> {marksTracker.remarks}</p>
                  <p><strong>Date Created:</strong> {marksTracker.dateCreated ? formatDateTime(marksTracker.dateCreated) : 'N/A'}</p>
                </div>
              )}

              {isDeleteClicked && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                  <div className="bg-gray-800 text-white p-6 rounded-lg shadow-lg">
                    <p className="text-lg mb-4">Are you sure you want to delete this Marks Tracker?</p>
                    <div className="flex gap-4">
                      <button className="bg-red-600 px-4 py-2 rounded-lg shadow hover:bg-red-700" onClick={() => handleDelete(marksTracker._id)}>Confirm Delete</button>
                      <button className="bg-yellow-500 px-4 py-2 rounded-lg shadow hover:bg-yellow-600" onClick={noDeleteClicked}>Cancel</button>
                    </div>
                    {deleted && (
                      <p className='text-yellow-200'>MarksTracker deleted successfully!</p>
                    )}
                    {deletedFailed && (
                      <p className='text-red-500'>Failed to delete MarksTracker!</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-teal-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
          </div>
      </footer>  
    </div>
  )
}

export default MarksTrackerList